import { type PropsWithChildren, useEffect, useRef } from 'react'

import { type Props as InputProps, Input } from '#/global/component/Form/Input'

const formatFn = (value: string, pattern: string) => {
    if (!pattern) {
        return value
    }

    const placeholder = '#'

    let endOfValue = 0
    let characterIndex = 0
    const newValue = value

    return [...pattern]
        .map((patternCharacter, index) => {
            const character = newValue[characterIndex]
            const patternLength = pattern.length

            if (!endOfValue) {
                if (index === patternLength - 1) {
                    endOfValue = patternLength
                }
                if (character === undefined) {
                    endOfValue = pattern.indexOf(placeholder, index)
                }
            }

            if (patternCharacter === placeholder) {
                characterIndex = characterIndex + 1
                return character
            }

            return patternCharacter
        })
        .splice(0, endOfValue)
        .join('')
}

const stripPatternCharacters = (value: string) => {
    return value.replaceAll(/[^\da-z]/gi, '')
}

const isUserCharacter = (character: string) => {
    return /[\da-z]/i.test(character)
}

interface Props extends InputProps {
    format: string
    type?: 'text' | 'tel'
}

export const InputFormat = ({
    children,
    format,
    onChange,
    value = '',
    ...props
}: PropsWithChildren<Props>) => {
    const inputRef = useRef<HTMLInputElement>(undefined)
    const infoRef = useRef<{
        cursorPosition: number | null
        endOfSection: boolean
    }>({
        cursorPosition: null,
        endOfSection: false,
    })

    const handleChange: Props['onChange'] = (inputValue, event) => {
        inputRef.current = event.target
        const cursorPosition = event.target.selectionStart ?? 0
        const didDelete = inputValue.length < value.length

        infoRef.current.cursorPosition = cursorPosition

        let rawValue = stripPatternCharacters(inputValue)

        if (didDelete) {
            const patternCharacterDeleted = !isUserCharacter([...value][cursorPosition])

            if (patternCharacterDeleted) {
                const firstBit = inputValue.slice(0, cursorPosition)
                const rawFirstBit = stripPatternCharacters(firstBit)

                rawValue = rawFirstBit.slice(0, -1) + stripPatternCharacters(inputValue.slice(cursorPosition, inputValue.length + cursorPosition))

                infoRef.current.cursorPosition = firstBit.replace(/(\w+)\s+$/, '$1').length - 1
            }
        }

        const formattedValue = formatFn(rawValue, format)

        infoRef.current.endOfSection = false

        if (!didDelete) {
            const formattedCharacters = [...formattedValue]
            const nextCharacter = formattedCharacters[cursorPosition]
            const nextCharacterIsPattern = !isUserCharacter(nextCharacter)
            const nextUserCharacterIndex = formattedValue.slice(cursorPosition).search(/[\da-z]/i)
            const numbersAhead = nextUserCharacterIndex !== -1

            infoRef.current.endOfSection = nextCharacterIsPattern && !numbersAhead

            if (
                nextCharacterIsPattern
                && !isUserCharacter(formattedCharacters[cursorPosition - 1])
                && numbersAhead
            ) {
                infoRef.current.cursorPosition = cursorPosition + nextUserCharacterIndex + 1
            }
        }

        onChange?.(formattedValue, event)
    }

    useEffect(() => {
        if (!inputRef.current) {
            return
        }

        const { cursorPosition, endOfSection } = infoRef.current

        if (endOfSection) {
            return
        }

        inputRef.current.setSelectionRange(cursorPosition, cursorPosition)
    }, [value])

    return (
        <Input
            onChange={handleChange}
            value={value}
            {...props}>
            {children}
        </Input>
    )
}
